import * as PopoverPrimitive from "@radix-ui/react-popover";
import type { NoteSourceSentence } from "@rosc/contracts";
import { Quote } from "lucide-react";
import type { JSX, MouseEvent, ReactNode } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { useNoteSources } from "@/hooks/visits/useVisitReview";
import { blockSourceState, sentenceAt } from "@/lib/review/noteSources";
import { usePhoneLayout } from "../layout";
import { SourceCard } from "./SourceCard";

// "Sources for every line" (v3.2, VITE_NOTE_SOURCES_ENABLED): wraps the read
// view of the main note. Clicking a sentence highlights it and opens its
// source: a popover anchored on the sentence (desktop) or a bottom sheet
// (phone). Self-contained: the note's own rendering is unchanged.

const BLOCKS = "p, li, td, th, blockquote, h1, h2, h3, h4, h5, h6";
const SKIP = "[data-verify-index], button, a, input, textarea";
const HIGHLIGHT = "rosc-note-source";
// Set on blocks that hold a sentence with a source (the guided tour's
// target): "all" when every sentence in the block has one, "some" otherwise.
export const SUPPORTED_ATTRIBUTE = "data-source-supported";

// Text nodes of a block in reading order, without verify chips.
const textNodes = (block: Element): Text[] => {
  const nodes: Text[] = [];
  const walker = document.createTreeWalker(block, NodeFilter.SHOW_TEXT, {
    acceptNode: (node) =>
      node.parentElement?.closest(SKIP)
        ? NodeFilter.FILTER_REJECT
        : NodeFilter.FILTER_ACCEPT,
  });
  for (let node = walker.nextNode(); node; node = walker.nextNode())
    nodes.push(node as Text);
  return nodes;
};

const caretAt = (
  x: number,
  y: number,
): { node: Node; offset: number } | null => {
  const doc = document as Document & {
    caretPositionFromPoint?: (
      x: number,
      y: number,
    ) => { offsetNode: Node; offset: number } | null;
    caretRangeFromPoint?: (x: number, y: number) => Range | null;
  };
  const position = doc.caretPositionFromPoint?.(x, y);
  if (position) return { node: position.offsetNode, offset: position.offset };
  const range = doc.caretRangeFromPoint?.(x, y);
  return range
    ? { node: range.startContainer, offset: range.startOffset }
    : null;
};

// A DOM range over [start, end) of the block's text.
const rangeFor = (nodes: Text[], start: number, end: number): Range | null => {
  const range = document.createRange();
  let seen = 0;
  let started = false;
  for (const node of nodes) {
    const length = node.data.length;
    if (!started && start <= seen + length) {
      range.setStart(node, Math.max(0, start - seen));
      started = true;
    }
    if (started && end <= seen + length) {
      range.setEnd(node, Math.max(0, end - seen));
      return range;
    }
    seen += length;
  }
  return null;
};

// Where in the note a source was opened from.
export type SourcePoint = { node: Node; offset: number };

type Selected = {
  sentence: NoteSourceSentence;
  // The click (or the sentence's start), for the source card's Edit.
  point: SourcePoint | null;
  rect: { top: number; left: number; width: number; height: number };
};

type HighlightRegistry = {
  set: (name: string, value: unknown) => void;
  delete: (name: string) => void;
};
const highlights = (): HighlightRegistry | null => {
  const css = (globalThis as { CSS?: { highlights?: HighlightRegistry } }).CSS;
  return typeof (globalThis as { Highlight?: unknown }).Highlight ===
    "function" && css?.highlights
    ? css.highlights
    : null;
};

export const NoteSourcesLayer = ({
  visitPublicId,
  result,
  onEdit,
  children,
}: {
  visitPublicId: string;
  result: string;
  // Click-to-edit (NoteDocument): the source card's Edit, which opens the
  // sentence editor for the sentence at `point`. A click that opens or
  // closes a source is taken here (preventDefault), so it does not also
  // open an editor.
  onEdit?: (point: SourcePoint | null) => void;
  children: ReactNode;
}): JSX.Element => {
  const { t } = useTranslation();
  const phone = usePhoneLayout();
  const root = useRef<HTMLDivElement>(null);
  const { data } = useNoteSources(visitPublicId, result, true);
  const [selected, setSelected] = useState<Selected | null>(null);
  // A source was open when this click began (the click closes it).
  const openAtPress = useRef(false);

  const clear = useCallback(() => {
    setSelected(null);
    highlights()?.delete(HIGHLIGHT);
  }, []);
  useEffect(() => clear, [clear]);
  // A changed note closes the card (its sentences moved).
  // biome-ignore lint/correctness/useExhaustiveDependencies: reset on note change
  useEffect(() => {
    clear();
  }, [result, clear]);

  // Mark the blocks that hold a sourced sentence. Runs again when the note or
  // its sources change; React keeps the attribute on unchanged blocks.
  // biome-ignore lint/correctness/useExhaustiveDependencies: re-mark on note change
  useEffect(() => {
    const container = root.current;
    if (!container) return;
    for (const block of container.querySelectorAll(BLOCKS)) {
      const text = textNodes(block)
        .map((node) => node.data)
        .join("");
      const state = data ? blockSourceState(text, data.sentences) : null;
      if (state) block.setAttribute(SUPPORTED_ATTRIBUTE, state);
      else block.removeAttribute(SUPPORTED_ATTRIBUTE);
    }
  }, [data, result]);

  const onClick = (event: MouseEvent<HTMLDivElement>): void => {
    if (openAtPress.current) event.preventDefault();
    if (!data || !root.current) return;
    const target = event.target as Element;
    if (target.closest(SKIP)) return;
    // Selecting text to copy is not a click on a sentence.
    if (window.getSelection()?.toString().trim()) return;
    const block = target.closest(BLOCKS);
    if (!block || !root.current.contains(block)) return;
    const nodes = textNodes(block);
    const text = nodes.map((node) => node.data).join("");
    const caret = caretAt(event.clientX, event.clientY);
    let offset = 0;
    if (caret) {
      for (const node of nodes) {
        if (node === caret.node) {
          offset += caret.offset;
          break;
        }
        offset += node.data.length;
      }
    }
    const hit = sentenceAt(text, offset, data.sentences);
    if (!hit) {
      clear();
      return;
    }
    event.preventDefault();
    const range = rangeFor(nodes, hit.start, hit.end);
    const registry = highlights();
    if (range && registry) {
      const Highlight = (
        globalThis as unknown as { Highlight: new (range: Range) => unknown }
      ).Highlight;
      registry.set(HIGHLIGHT, new Highlight(range));
    }
    const box = root.current.getBoundingClientRect();
    const rects =
      range && typeof range.getClientRects === "function"
        ? Array.from(range.getClientRects())
        : [];
    // Anchor on the sentence's last line, so the card opens just below it.
    const line = rects[rects.length - 1] ?? block.getBoundingClientRect();
    setSelected({
      sentence: hit.sentence,
      point:
        caret ??
        (range
          ? { node: range.startContainer, offset: range.startOffset }
          : null),
      rect: {
        top: line.top - box.top,
        left: (rects[0]?.left ?? line.left) - box.left,
        width: Math.max(1, line.right - (rects[0]?.left ?? line.left)),
        height: line.height,
      },
    });
  };

  const card =
    selected && data ? (
      <SourceCard
        sentence={selected.sentence}
        showClose={!phone}
        sources={data}
        onClose={clear}
        onEdit={
          onEdit
            ? () => {
                const point = selected.point;
                clear();
                onEdit(point);
              }
            : undefined
        }
      />
    ) : null;

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: a click on the note's text picks a sentence; the source is extra context, and the note stays fully readable and copyable without it.
    <div
      ref={root}
      className="relative"
      data-sources={data ? "ready" : "loading"}
      data-testid="note-sources"
      onClick={onClick}
      onPointerDownCapture={() => {
        openAtPress.current = !!selected;
      }}
      onKeyDown={(event) => {
        if (event.key === "Escape") clear();
      }}
    >
      <style>{`::highlight(${HIGHLIGHT}){background-color:var(--v3-brand-tint-2);color:inherit}
[data-sources="ready"] :is(p,li,td,blockquote){cursor:pointer;border-radius:6px;transition:background-color .15s}
@media (hover:hover){[data-sources="ready"] :is(p,li,td,blockquote):hover{background-color:color-mix(in srgb,var(--v3-surface-2) 70%,transparent)}}`}</style>
      {data && (
        <p className="mb-3 flex items-center gap-1.5 text-[12.5px] text-v3-text-3">
          <Quote aria-hidden className="size-3.5" />
          {t("visit.sources.hint")}
        </p>
      )}
      {children}
      {!phone && (
        <PopoverPrimitive.Root
          open={!!selected}
          onOpenChange={(open) => {
            if (!open) clear();
          }}
        >
          <PopoverPrimitive.Anchor asChild>
            <span
              aria-hidden
              className="pointer-events-none absolute"
              style={
                selected
                  ? {
                      top: selected.rect.top,
                      left: selected.rect.left,
                      width: selected.rect.width,
                      height: selected.rect.height,
                    }
                  : { top: 0, left: 0, width: 0, height: 0 }
              }
            />
          </PopoverPrimitive.Anchor>
          <PopoverPrimitive.Portal>
            <PopoverPrimitive.Content
              align="start"
              aria-label={t("visit.sources.title")}
              className="z-50 w-[340px] max-w-[calc(100vw-24px)] rounded-xl bg-v3-panel px-3.5 pb-3 pt-2 shadow-v3-pop outline-none data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95"
              collisionPadding={12}
              side="bottom"
              sideOffset={6}
              onOpenAutoFocus={(event) => {
                event.preventDefault();
              }}
              // It never took focus, so it gives none back on close (Edit
              // hands focus to the sentence editor).
              onCloseAutoFocus={(event) => {
                event.preventDefault();
              }}
            >
              {card}
            </PopoverPrimitive.Content>
          </PopoverPrimitive.Portal>
        </PopoverPrimitive.Root>
      )}
      {phone && (
        <Sheet
          open={!!selected}
          onOpenChange={(open) => {
            if (!open) clear();
          }}
        >
          <SheetContent
            aria-describedby={undefined}
            className="max-h-[80dvh] gap-0 overflow-y-auto rounded-t-2xl bg-v3-panel px-4 pb-[max(16px,env(safe-area-inset-bottom))] pt-2 [&_button.absolute]:w-11!"
            data-testid="source-sheet"
            side="bottom"
          >
            <div
              aria-hidden
              className="mx-auto mb-2 h-1 w-10 rounded-full bg-v3-text-3/40"
            />
            <SheetTitle className="sr-only">
              {t("visit.sources.title")}
            </SheetTitle>
            {selected && (
              <p className="mb-3 pr-10 text-[15px] font-medium leading-[21px] text-v3-text">
                {selected.sentence.text}
              </p>
            )}
            {card}
          </SheetContent>
        </Sheet>
      )}
    </div>
  );
};
